import { Shop } from '@@types'
import { Clusterer, Map, Placemark, YMaps } from '@pbe/react-yandex-maps'
import { useEffect, useMemo, useRef, useState } from 'react'

import { BRAND_CONFIG } from '@@brand'
import { useLazyShopsInGeoPolygon } from '@entities/Shop/api/shopsInGeoPolygon.gql'

import styles from './PickupView.module.scss'


// Координаты, покрывающие весь земной шар
const GLOBAL_CORNERS = [
  { latitude: -90, longitude: -180 },
  { latitude: -90, longitude: 180 },
  { latitude: 90, longitude: 180 },
  { latitude: 90, longitude: -180 },
]

const DEFAULT_CENTER: [number, number] = [ 50.300377, 57.154555 ]

export default function MapPickup ({
  center,
  onShopsDataUpdate,
  onShopClick,
}: {
  center?: [number, number] | null;
  onShopsDataUpdate: (data: Shop[]) => void;
  onShopClick?: (shop: Shop) => void;
}) {
  const mapRef = useRef<ymaps.Map | null>(null)
  const [ isMapReady, setMapReady ] = useState(false)
  const [ mapCenter ] = useState<[number, number]>(DEFAULT_CENTER)
  const [ shops, setShops ] = useState<Shop[]>([])
  const [ fetchShops, { data } ] = useLazyShopsInGeoPolygon()
  

  const filteredShops = useMemo(() => {
    return (
      data?.shopsInGeoPolygon?.filter(
        (shop) => shop?.isPickupDeliveryAvailable,
      ) || []
    )
  }, [ data?.shopsInGeoPolygon ])

  // Загружаем магазины один раз при монтировании
  useEffect(() => {
    fetchShops({ variables: { corners: GLOBAL_CORNERS } })
  }, [ fetchShops ])

  // Сохраняем отфильтрованные магазины в стейт
  useEffect(() => {
    if (filteredShops.length > 0) {
      setShops(filteredShops)
    }
  }, [ filteredShops ])

  useEffect(() => {
    onShopsDataUpdate(shops)
  }, [ shops, onShopsDataUpdate ])

  useEffect(() => {
    if (center && isMapReady && mapRef.current) {
      mapRef.current.setCenter(center, 14, {
        duration: 300,
      })
    }
  }, [ center, isMapReady ])

  return (
    <div className={styles.map}>
      <YMaps
        query={{
          apikey: import.meta.env.VITE_YANDEX_MAPS_API_KEY,
          load: 'package.full',
        }}
      >
        <Map
          defaultState={{
            center: mapCenter,
            zoom: 4,
            controls: [ 'zoomControl' ],
          }}
          width="100%"
          height="100%"
          instanceRef={(ref) => {
            if (ref) {
              mapRef.current = ref
              setMapReady(true)
            }
          }}
          options={{
            suppressMapOpenBlock: true,
            copyrightLogoVisible: false,
            copyrightUaVisible: false,
          }}
        >
          <Clusterer
            options={{
              preset: 'islands#invertedRedClusterIcons',
              groupByCoordinates: false,
              clusterDisableClickZoom: false,
              clusterOpenBalloonOnClick: false,
            }}
          >
            {filteredShops?.map((shop) => {
              const latitude = shop?.shopGeoPoint?.latitude
              const longitude = shop?.shopGeoPoint?.longitude
              if (latitude == null || longitude == null) return null

              return (
                <Placemark
                  onClick={() => {
                    if (onShopClick) onShopClick(shop)
                  }}
                  key={shop?.id}
                  geometry={[ latitude, longitude ]}
                  options={{
                    iconLayout: 'default#image',
                    iconImageHref: BRAND_CONFIG.images.mapPin,
                    iconImageSize: [ 36, 56 ],
                    iconImageOffset: [ -16, -32 ],
                  }}
                />
              )
            })}
          </Clusterer>
        </Map>
      </YMaps>
    </div>
  )
}
